/** Market tab: the Contracts board, then the Hold with today's prices. */

import {
  ALL_RESOURCES, FIT_MAX_LEVEL, FLEET_STAT_LABEL, ITEMS, RARITY_LABEL, fitBonus, fitLevel, resource,
  type ResourceDef, type ResourceKind,
} from '../../content/resources';
import { copiesToNextLevel, fitRoom } from '../../sim/actions';
import { ADDITIONS } from '../../content/flags';
import { sellPrice } from '../../sim/economy';
import { contractReward, hotResource, marketForecast, marketMult, marketSecondsLeft } from '../../sim/market';
import { esc, fmtNum, fmtTime, price } from '../format';
import { btn, held, resGlyph, resName, type HoldView, type View } from '../view';

/** "▲ 32%" / "▼ 18%" / "🔥 ×2.5" against the base price. */
/** Market Insight (IPM): the next period's swing, shown after the current one. */
function forecast(v: View, id: string): string {
  if (!v.state.run.projects.includes('market_insight')) return '';
  const pct = Math.round((marketForecast(v.state, id) - 1) * 100);
  const sign = Math.abs(pct) < 3 ? '—' : `${pct > 0 ? '▲' : '▼'}${Math.abs(pct)}%`;
  return ` <span class="vm-trend" title="Next market">next ${sign}</span>`;
}

function trend(v: View, id: string): string {
  return trendNow(v, id) + forecast(v, id);
}

function trendNow(v: View, id: string): string {
  if (id === hotResource(v.state)) return '<span class="vm-trend hot">HOT ×2.5</span>';
  const m = marketMult(v.state, id);
  const pct = Math.round((m - 1) * 100);
  if (Math.abs(pct) < 3) return '<span class="vm-trend">—</span>';
  return `<span class="vm-trend ${pct > 0 ? 'up' : 'down'}">${pct > 0 ? '▲' : '▼'} ${Math.abs(pct)}%</span>`;
}

function contractsBoard(v: View): string {
  const { state } = v;
  const cards = state.contracts.map((c, i) => {
    if (!c) {
      const wait = Math.max(0, (state.contractReadyAt[i] ?? 0) - state.clock);
      const none = !state.discovered.some((id) => resource(id).kind !== 'ore');
      return `<div class="vm-contract empty" data-key="ct-${i}"><div class="vm-faint">${none ? 'Smelt your first bar to open contracts' : `New contract in ${fmtTime(wait)}`}</div></div>`;
    }
    const have = Math.floor(held(state, c.item));
    const ready = have >= c.qty;
    return `<div class="vm-contract${ready ? ' ready' : ''}" data-key="ct-${i}">
      <div class="vm-contract-item">${resGlyph(c.item)}<b>${c.qty}× ${esc(resource(c.item).name)}</b></div>
      <div class="vm-contract-meta"><span class="${ready ? 'ok' : ''}">${Math.min(have, c.qty)}/${c.qty}</span> · pays ${price('vs', contractReward(state, c))}</div>
      <div class="vm-btns">
        ${btn('✕', 'discardContract', { cls: 'ghost', data: { slot: i } })}
        ${btn('Deliver', 'deliverContract', { cls: ready ? 'magenta' : 'ghost', disabled: !ready, data: { slot: i } })}
      </div>
    </div>`;
  }).join('');
  return `<section class="vm-section" data-key="contracts">
    <div class="vm-section-head">Contracts <span class="aside">Void Shards for the Matrix</span></div>
    <div class="vm-contracts">${cards}</div>
  </section>`;
}

function stockRow(v: View, def: ResourceDef): string {
  const { state, stats } = v;
  const qty = held(state, def.id);
  const each = sellPrice(state, stats, def.id);
  return `<div class="vm-row" style="--accent:${def.color}" data-key="st-${def.id}">
    <div class="vm-row-main">
      <div class="vm-row-name">${resName(def.id)} ${trend(v, def.id)}</div>
      <div class="vm-row-effect"><b>${fmtNum(qty)}</b> · ${price('gc', each)} each · ${price('gc', qty * each)}</div>
    </div>
    <div class="vm-btns">${btn('Sell', 'sell', { cls: 'gold', disabled: qty < 1e-6, data: { id: def.id, qty: 'all' } })}</div>
  </div>`;
}

function fitText(def: ResourceDef, level: number): string {
  const bonus = fitBonus(def.rarity!, Math.max(1, level));
  return (def.fits ?? []).map((s) => `+${Math.round(bonus * 100)}% ${FLEET_STAT_LABEL[s]}`).join(' · ');
}

function itemRow(v: View, def: ResourceDef): string {
  const { state, stats } = v;
  const qty = Math.floor(held(state, def.id));
  const level = fitLevel(state.fittings[def.id] ?? 0);
  const toNext = copiesToNextLevel(state, def.id);
  const room = fitRoom(state, def.id);
  return `<div class="vm-row" style="--accent:${def.color}" data-key="it-${def.id}">
    <div class="vm-row-main">
      <div class="vm-row-name">${resName(def.id)} ${trend(v, def.id)}</div>
      <div class="vm-row-effect"><b>${qty}</b> · ${price('gc', sellPrice(state, stats, def.id))} each · <span style="color:${def.color}">${RARITY_LABEL[def.rarity!]}</span>${ADDITIONS.fittings ? `<br>
        Fit${level ? ` Lv ${level}` : ''}: ${fitText(def, level + (level < FIT_MAX_LEVEL ? 1 : 0))}${toNext > 0 ? ` (${toNext} more)` : ''}` : ''}</div>
    </div>
    <div class="vm-btns">
      ${btn('Sell', 'sell', { cls: 'gold', disabled: qty < 1, data: { id: def.id, qty: 1 } })}
      ${ADDITIONS.fittings ? btn(level >= FIT_MAX_LEVEL ? 'Max' : 'Fit', 'fit', { cls: 'violet', disabled: qty < 1 || room <= 0, data: { id: def.id } }) : ''}
    </div>
  </div>`;
}

export function renderMarket(v: View): string {
  const { state, ui } = v;
  const kind: ResourceKind = ui.holdView;
  const segs = (['ore', 'alloy', 'item'] as const).map((t) =>
    btn({ ore: 'Ores', alloy: 'Bars', item: 'Items' }[t], 'holdView', { cls: ui.holdView === t ? 'on' : 'ghost', data: { view: t } })).join('');
  const defs = ALL_RESOURCES.filter((d) => d.kind === kind && state.discovered.includes(d.id));
  const rows = defs.map((d) => (kind === 'item' ? itemRow(v, d) : stockRow(v, d))).join('');
  const total = defs.reduce((sum, d) => sum + held(state, d.id) * sellPrice(state, v.stats, d.id), 0);
  const sellAll = kind === 'item' ? '' : btn(`Sell all ${kind === 'ore' ? 'ores' : 'bars'} · ${price('gc', total)}`, 'sellKind', { cls: 'gold wide', disabled: total < 1, data: { kind } });
  const empty = kind === 'item'
    ? `Nothing crafted yet. ${ITEMS.slice(0, 6).map((d) => resGlyph(d.id)).join(' ')}…`
    : 'Nothing here yet.';
  return `${ADDITIONS.contracts ? contractsBoard(v) : ''}
    <section class="vm-section" data-key="hold">
      <div class="vm-section-head gold">Hold <span class="aside">prices shift in ${fmtTime(marketSecondsLeft(state))}</span></div>
      <div class="vm-seg" style="margin-bottom:8px">${segs}</div>
      ${sellAll ? `<div style="margin-bottom:8px">${sellAll}</div>` : ''}
      <div class="vm-rows">${rows || `<p class="vm-note">${empty}</p>`}</div>
    </section>
    <p class="vm-note vm-faint">Bars and items left in the hold are distilled into a little TE at the Fold.${ADDITIONS.fittings ? " Fitting an item makes its bonus permanent." : ""}</p>`;
}

export type { HoldView };
